import { CREATE_EXPERT, UPDATE_EXPERT } from '../graphql/expert'
import ReferralPartner from './referralPartner'

class ExpertProfile {
  static #generateProfile(profileInput) {
    return {
      first_name: profileInput.first_name,
      last_name: profileInput.last_name,
      country: profileInput.country,
      employmentHistory: profileInput.employmentHistory.map((e) => ({
        ...e,
        organization: e.organization.id,
      })),
      publicLinks: profileInput.publicLinks
        ? Array.from(new Set(profileInput.publicLinks.filter((url) => !!url)))
        : undefined,
      credentials: profileInput.credentials,
      professionalRegistrations: (profileInput.professionalRegistrations || [])
        .filter((registration) => registration.number?.trim())
        .map((registration) => ({
          ...registration,
          number: registration.number.trim(),
        })),
      gender: profileInput.gender,
      yearOfBirth: profileInput.yearOfBirth || undefined,
      education: profileInput.education,
      ethnicity: profileInput.ethnicity,
      language: profileInput.language,
      veteran_status: profileInput.veteran_status,
      disability_status: profileInput.disability_status,
      hourlyRate: profileInput.hourlyRate,
      functions: profileInput.functions,
      technology_tags: profileInput.technology_tags,
      medical_specialties: profileInput.medical_specialties,
      description: profileInput.description,
    }
  }

  static hasInvalidFields(fields) {
    if (fields.first_name === '') {
      return 'Please fill out your first name.'
    }
    if (fields.last_name === '') {
      return 'Please fill out your last name.'
    }
    if (fields.country === '') {
      return 'Please select your country.'
    }
    if (
      fields.employmentHistory &&
      (fields.employmentHistory.length === 0 ||
        fields.employmentHistory.some((e) => !e.title || !e.organization.id))
    ) {
      return 'Please fill out your employment information completely'
    }
    const publicLinks = fields.publicLinks?.filter((url) => url?.trim()) || []
    if (publicLinks.length > 0) {
      const hasInvalidURL = publicLinks.some((url) => {
        try {
          const parsed = new URL(url)
          return parsed.protocol !== 'http:' && parsed.protocol !== 'https:'
        } catch {
          return true
        }
      })

      if (hasInvalidURL) {
        return 'Please make sure all the URLs are valid and start with http or https'
      }

      const hasInvalidLinkedinURL = publicLinks.some(
        (url) =>
          url.includes('linkedin.com') &&
          !url.startsWith('https://www.linkedin.com')
      )
      if (hasInvalidLinkedinURL) {
        return 'Please fill out valid Linkedin URL'
      }
    }

    if (fields.hourlyRate === '' || fields.hourlyRate === 0) {
      return 'Please fill out your hourly rate.'
    }
    return null
  }

  static async create(client, profileInput, onCompleted) {
    const { data } = await client.mutate({
      mutation: CREATE_EXPERT,
      variables: {
        profile: this.#generateProfile(profileInput),
        notifications: {
          status: 'Active',
          reminder_notify: true,
          payment_notify: true,
        },
      },
      // The register page reports the failure in the form it came from, so the
      // expert keeps the answers they already gave.
      context: { skipErrorDisplay: true },
    })

    // The partner went out with the signup request and now lives on the user record. Drop the
    // browser copy once registration is done, so a shared browser can't attribute the next
    // person who signs up on it.
    ReferralPartner.clear()

    if (onCompleted) {
      await onCompleted()
    }

    return data.createExpert
  }

  static async update(client, profileInput, onCompleted) {
    const { data } = await client.mutate({
      mutation: UPDATE_EXPERT,
      variables: {
        input: this.#generateProfile(profileInput),
      },
    })

    if (onCompleted) {
      await onCompleted()
    }

    return data.updateExpert
  }
}

export default ExpertProfile
